

import React, { useState } from 'react';
import {
  Modal,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from 'assets/theme/base/colors';

const NewLoyaltyTierModal = ({ isOpen, onClose, onSave }) => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    tier_code: '',
    tier_rank: 1,
    status: 'Active',
    redemption_conversion: '',
    override_rule: true,
    earn_multiplier: '1.00',
    qualify_metric: 'Points',
    qualify_threshold: '',
    validity_months: 12,
    benefits: '',
    notes: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) errors.name = 'Tier Name is required';
    if (!formData.tier_code) errors.tier_code = 'Tier Code is required';
    if (!formData.tier_rank) errors.tier_rank = 'Tier Rank is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        tier_rank: formData.tier_rank ? parseInt(formData.tier_rank, 10) : 1,
        redemption_conversion: formData.redemption_conversion
          ? parseFloat(formData.redemption_conversion)
          : null,
        earn_multiplier: formData.earn_multiplier
          ? parseFloat(formData.earn_multiplier)
          : null,
        qualify_threshold: formData.qualify_threshold
          ? parseFloat(formData.qualify_threshold)
          : null,
        validity_months: formData.validity_months
          ? parseInt(formData.validity_months, 10)
          : null,
        created_by: user?.id,
        updated_by: user?.id,
      };

      await onSave(payload);
      onClose();
      setFormData({
        name: '',
        tier_code: '',
        tier_rank: 1,
        status: 'Active',
        redemption_conversion: '',
        override_rule: true,
        earn_multiplier: '1.00',
        qualify_metric: 'Points',
        qualify_threshold: '',
        validity_months: 12,
        benefits: '',
        notes: '',
      });
    } catch (error) {
      console.error('Error saving Loyalty Tier:', error);
      setToast({
        open: true,
        message: error.response?.data?.detail || 'Error saving Loyalty Tier',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: '700px' },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 3,
              py: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <MDTypography variant="h6" color="white">
              Add New Loyalty Tier
            </MDTypography>
            <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 4, overflowY: 'auto' }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tier Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tier Code"
                    name="tier_code"
                    value={formData.tier_code}
                    onChange={handleInputChange}
                    error={!!formErrors.tier_code}
                    helperText={formErrors.tier_code}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tier Rank"
                    name="tier_rank"
                    type="number"
                    value={formData.tier_rank}
                    onChange={handleInputChange}
                    error={!!formErrors.tier_rank}
                    helperText={formErrors.tier_rank}
                    required
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      label="Status"

                    >
                      <MenuItem value="Draft">Draft</MenuItem>
                      <MenuItem value="Active">Active</MenuItem>
                      <MenuItem value="Retired">Retired</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Redemption Conversion (AUD/Point)"
                    name="redemption_conversion"
                    type="number"
                    value={formData.redemption_conversion}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox mt={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.override_rule}
                          onChange={handleInputChange}
                          name="override_rule"
                        />
                      }
                      label="Override Base Rule"
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Earn Multiplier"
                    name="earn_multiplier"
                    type="number"
                    value={formData.earn_multiplier}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Qualify Metric</InputLabel>
                    <Select
                      name="qualify_metric"
                      value={formData.qualify_metric}
                      onChange={handleInputChange}
                      label="Qualify Metric"

                    >
                      <MenuItem value="Points">Points</MenuItem>
                      <MenuItem value="Trips">Trips</MenuItem>
                      <MenuItem value="Revenue">Revenue</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Qualify Threshold"
                    name="qualify_threshold"
                    type="number"
                    value={formData.qualify_threshold}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Validity (Months)"
                    name="validity_months"
                    type="number"
                    value={formData.validity_months}
                    onChange={handleInputChange}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Benefits"
                    name="benefits"
                    value={formData.benefits}
                    onChange={handleInputChange}
                    multiline
                    minRows={2}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    multiline
                    minRows={2}
                    variant="outlined"
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </MDButton>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NewLoyaltyTierModal;


